import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://itsmyvibe-backend-production.up.railway.app';
const API_BASE = `${BACKEND_URL}/api`;

// Debug - remove after fixing
console.log('BACKEND_URL:', JSON.stringify(BACKEND_URL));
console.log('API_BASE:', JSON.stringify(API_BASE));

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('itsmyvibe_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors and retries
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 - token expired
    if (error.response?.status === 401) {
      localStorage.removeItem('itsmyvibe_token');
      localStorage.removeItem('itsmyvibe_user');
      localStorage.removeItem('itsmyvibe_encryption_key');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle network errors, timeouts, or 5xx errors with retry
    if (
      !originalRequest._retry && 
      (
        error.code === 'ECONNABORTED' || // timeout
        error.code === 'ERR_NETWORK' ||  // network error
        !error.response ||               // no response (network issue)
        (error.response?.status >= 500)  // server error
      )
    ) {
      originalRequest._retry = true;
      
      // Wait 1 second before retry
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return apiClient(originalRequest);
    }

    return Promise.reject(error);
  }
);

// Projects API
export const projectsApi = {
  getAll: () => apiClient.get('/projects'),
  getById: (id) => apiClient.get(`/projects/${id}`),
  create: (data) => apiClient.post('/projects', data),
  update: (id, data) => apiClient.put(`/projects/${id}`, data),
  delete: (id) => apiClient.delete(`/projects/${id}`),
};

// Prompts API
export const promptsApi = {
  getByProject: (projectId) => apiClient.get(`/projects/${projectId}/prompts`),
  getById: (id) => apiClient.get(`/prompts/${id}`),
  create: (projectId, data) => apiClient.post(`/projects/${projectId}/prompts`, data),
  update: (id, data) => apiClient.put(`/prompts/${id}`, data),
  delete: (id) => apiClient.delete(`/prompts/${id}`),
  improve: (data) => apiClient.post('/improve-prompt', data),
};

// System Prompts API
export const systemPromptsApi = {
  getAll: () => apiClient.get('/system-prompts'),
  getById: (id) => apiClient.get(`/system-prompts/${id}`),
  create: (data) => apiClient.post('/system-prompts', data),
  update: (id, data) => apiClient.put(`/system-prompts/${id}`, data),
  delete: (id) => apiClient.delete(`/system-prompts/${id}`),
};

// Settings API
export const settingsApi = {
  get: () => apiClient.get('/settings'),
  update: (data) => apiClient.put('/settings', data),
};

// Footnotes API
export const footnotesApi = {
  getAll: () => apiClient.get('/footnotes'),
  getById: (id) => apiClient.get(`/footnotes/${id}`),
  create: (data) => apiClient.post('/footnotes', data),
  update: (id, data) => apiClient.put(`/footnotes/${id}`, data),
  delete: (id) => apiClient.delete(`/footnotes/${id}`),
};

// Auth API
export const authApi = {
  register: (data) => apiClient.post('/register', data),
  login: (data) => apiClient.post('/login', data),
  me: () => apiClient.get('/me'),
};

export default apiClient;